import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Heart, MessageCircle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const BlogCardMedium = ({ blog, noGrid = false }) => {
  const linkClass = noGrid 
    ? "group cursor-pointer block h-full"
    : "group col-span-12 sm:col-span-6 lg:col-span-4 cursor-pointer";
  
  return (
    <Link
      to={`/blogs/${blog.slug || blog._id}`}
      className={linkClass}
    >
      <div className="card h-full flex flex-col overflow-hidden min-h-[400px]">
        {/* Thumbnail */}
        <div className="relative w-full h-48 md:h-56 overflow-hidden bg-gray-200 dark:bg-gray-700">
          <img
            src={blog.thumbnail}
            alt={blog.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/800x400?text=Blog+Image';
            }}
          />
          {blog.featured && (
            <span className="absolute top-2 left-2 bg-accent text-white px-2 py-1 rounded text-xs font-medium">
              Featured
            </span>
          )}
          {blog.sponsored && (
            <span className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded text-xs font-medium">
              Sponsored
            </span>
          )}
        </div>

        {/* Content */}
        <div className="p-4 flex-1 flex flex-col">
          <span className="text-xs font-medium text-accent mb-2">
            {blog.category}
          </span>
          <h3 className="text-lg font-heading font-bold text-heading dark:text-white mb-2 line-clamp-2 group-hover:text-accent transition-colors">
            {blog.title}
          </h3>
          {blog.excerpt && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
              {blog.excerpt}
            </p>
          )}
          <div className="mt-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                {blog.author?.profile?.avatar ? (
                  <img
                    src={blog.author.profile.avatar}
                    alt={blog.author.username}
                    className="w-6 h-6 rounded-full"
                  />
                ) : (
                  <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center text-white text-xs font-medium">
                    {blog.author?.username?.charAt(0).toUpperCase() || 'A'}
                  </div>
                )}
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {blog.author?.username || 'Anonymous'}
                </span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-500 flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                {blog.publishedAt
                  ? formatDistanceToNow(new Date(blog.publishedAt), { addSuffix: true })
                  : formatDistanceToNow(new Date(blog.createdAt), { addSuffix: true })}
              </span>
            </div>
            <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-500">
              <div className="flex items-center">
                <Eye className="w-4 h-4 mr-1" />
                {blog.views || 0}
              </div>
              <div className="flex items-center">
                <Heart className="w-4 h-4 mr-1" />
                {blog.likes || 0}
              </div>
              <div className="flex items-center">
                <MessageCircle className="w-4 h-4 mr-1" />
                {blog.comments?.length || 0}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default BlogCardMedium;
